import React from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { documentService } from '@/services/document.service';
import {
  documentTrailService,
  isTrailEntityType,
  trailSourceLabel,
  trailSourcePath,
  type InheritedAttachmentGroup,
  type InheritedDocument,
  type TrailDomain,
} from '@/services/documentTrail.service';

const { Text } = Typography;

export interface InheritedAttachmentsCardProps {
  trailDomain: TrailDomain;
  entityType: string;
  entityId: string | null | undefined;
  queryKeyPrefix?: string[];
  style?: React.CSSProperties;
  /** Render the card with this message instead of hiding it when there is nothing to inherit. */
  emptyText?: string;
}

async function handleDownload(doc: InheritedDocument) {
  try {
    const blob = await documentService.downloadDocument(doc.id);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.file_name || 'download';
    a.click();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 2000);
  } catch {
    message.error('Download failed');
  }
}

async function handleView(doc: InheritedDocument) {
  try {
    await documentService.openDocumentInNewTab(doc.id);
  } catch (e: unknown) {
    const msg =
      e && typeof e === 'object' && 'message' in e ? String((e as { message: string }).message) : 'View failed';
    message.error(msg);
  }
}

const columns: ColumnsType<InheritedDocument> = [
  {
    title: 'File',
    dataIndex: 'file_name',
    ellipsis: true,
    render: (_: unknown, r) => (
      <Space size={4}>
        <span>{r.file_name || '—'}</span>
        {r.tag ? <Tag color="blue">{r.tag}</Tag> : null}
      </Space>
    ),
  },
  {
    title: 'Size',
    width: 90,
    render: (_: unknown, r) => documentService.formatFileSize(Number(r.file_size_bytes || 0)),
  },
  {
    title: 'Uploaded',
    width: 150,
    render: (_: unknown, r) => (r.created_at ? String(r.created_at).slice(0, 19).replace('T', ' ') : '—'),
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 88,
    align: 'right',
    render: (_: unknown, r) => (
      <Space size={0} wrap={false}>
        <Tooltip title="View">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => void handleView(r)} />
        </Tooltip>
        <Tooltip title="Download">
          <Button type="link" size="small" icon={<DownloadOutlined />} onClick={() => void handleDownload(r)} />
        </Tooltip>
      </Space>
    ),
  },
];

function GroupTitle({ domain, group }: { domain: TrailDomain; group: InheritedAttachmentGroup }) {
  const path = trailSourcePath(domain, group.source_type, group.source_id);
  const label = `${trailSourceLabel(domain, group.source_type)}${group.source_number ? ` ${group.source_number}` : ''}`;
  return (
    <Space size={8} style={{ marginBottom: 4 }}>
      {path ? <Link to={path}>{label}</Link> : <Text strong>{label}</Text>}
      {group.source_status ? <Tag>{group.source_status}</Tag> : null}
      <Text type="secondary">
        {group.documents.length} file{group.documents.length === 1 ? '' : 's'}
      </Text>
    </Space>
  );
}

/** Read-only attachments of upstream documents in the chain, resolved live on the server. */
export const InheritedAttachmentsCard: React.FC<InheritedAttachmentsCardProps> = ({
  trailDomain,
  entityType,
  entityId,
  queryKeyPrefix = ['documents', 'trail'],
  style,
  emptyText,
}) => {
  const enabled = Boolean(entityId) && isTrailEntityType(trailDomain, entityType);
  const { data: groups = [], isLoading, isError } = useQuery({
    queryKey: [...queryKeyPrefix, 'inherited', trailDomain, entityType, entityId ?? ''],
    queryFn: () => documentTrailService.getInheritedAttachments(trailDomain, entityType, entityId!),
    enabled,
  });

  const isEmpty = !isError && groups.length === 0;
  if (!enabled || (isEmpty && !emptyText) || (isLoading && !emptyText)) return null;

  return (
    <Card size="small" title="From earlier documents" loading={isLoading} style={{ marginTop: 12, ...style }}>
      {isError ? (
        <Alert type="warning" showIcon message="Could not load attachments from earlier documents." />
      ) : isEmpty ? (
        <Text type="secondary">{emptyText}</Text>
      ) : (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {groups.map((g) => (
            <div key={`${g.source_type}:${g.source_id}`}>
              <GroupTitle domain={trailDomain} group={g} />
              <Table<InheritedDocument>
                size="small"
                rowKey="id"
                dataSource={g.documents}
                pagination={false}
                columns={columns}
                scroll={{ x: 560 }}
              />
            </div>
          ))}
        </Space>
      )}
    </Card>
  );
};

export default InheritedAttachmentsCard;
